import type { PropertyFilterOperator } from '@cloudscape-design/collection-hooks';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import BadgeList from 'src/components/badge-list';
import type { useGetChangeRequests } from 'src/hooks/queries/change-request/useGetChangeRequests';

import Link from '@/components/link';
import useEntityInfo from '@/hooks/getEntityInfo';
import { getFriendlyId } from '@/utils/friendlyId';
import { useGetApproversFieldConfig } from '@/utils/table/hooks/useGetApproversFieldConfig';
import { useGetCurrentApproversFieldConfig } from '@/utils/table/hooks/useGetCurrentApproversFieldConfig';
import { useGetNextApproversFieldConfig } from '@/utils/table/hooks/useGetNextApproversFieldConfig';
import { useGetParentOwnersFieldConfig } from '@/utils/table/hooks/useGetParentOwnersFieldConfig';
import { useGetRequestersFieldConfig } from '@/utils/table/hooks/useGetRequestersFieldConfig';
import type {
  StatefulTableOptions,
  UseGetTablePropsOptions,
} from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { yesNoColumn } from '@/utils/table/utils/yesNoFilter';
import { policyFileDetailsUrl } from '@/utils/urls';

import type { ChangeRequestRegisterFields } from './types';
import { useLabelledFields } from './useLabelledFields';

type GetChangeRequestsData = NonNullable<
  ReturnType<typeof useGetChangeRequests>['data']
>;

export const useGetChangeRequestParentUrl = () => {
  const getEntityInfo = useEntityInfo();
  const getParentUrl = (changeRequest: {
    Workflow?: string | null;
    // RSP-6121 — V3 acceptance CRs parent the risk and pin the edited
    // acceptance id in `RequestedChanges.Id`. jsonb is `any` in the generated
    // types; narrow to the only key we read here.
    RequestedChanges?: { Id?: string | null } | null;
    parent?: {
      Id: string;
      ObjectType?: ParentType | null;
      documentFile?: { parent?: { Id: string } | null } | null;
      issue_assessment?: { parent?: { Id: string } | null } | null;
    } | null;
  }): string => {
    const { Workflow, RequestedChanges, parent } = changeRequest;

    // ── Route 1 — parent-walking acceptance workflows ──────────────────────
    // open/delete-acceptance CRs are *about* an acceptance, but their parent
    // node is the APPROVAL parent (the risk, V3) or the acceptance itself (V2),
    // so routing by `parent.ObjectType` alone would land a V3 CR on the risk
    // page. Route by Workflow and resolve the acceptance id from whichever
    // stored shape applies (cross-generation): the acceptance node directly
    // (V2) or the id pinned in `RequestedChanges.Id` (V3).
    const acceptanceCrUrl = (): string => {
      const acceptanceId =
        parent?.ObjectType === Parent_Type_Enum.Acceptance
          ? parent.Id
          : RequestedChanges?.Id;

      return acceptanceId
        ? getEntityInfo(Parent_Type_Enum.Acceptance).url(acceptanceId)
        : '';
    };

    switch (Workflow) {
      case 'open-acceptance':
      case 'delete-acceptance':
        return acceptanceCrUrl();
      default:
        break; // fall through to parent-type routing
    }

    // ── Route 2 — every other CR routes by its parent node type ────────────
    if (!parent?.ObjectType) {
      return '';
    }

    switch (parent.ObjectType) {
      case 'document_file':
        // The CR parents the document version; walk up to the policy page.
        return parent.documentFile?.parent
          ? policyFileDetailsUrl(parent.documentFile.parent.Id, parent.Id)
          : '';
      case 'issue_assessment':
        // Walk up to the parent issue.
        return getEntityInfo(parent.ObjectType).url(
          parent.issue_assessment?.parent?.Id ?? '#'
        );
      default:
        // Self-parented CR (risk, control, action, …): parent IS the entity.
        return getEntityInfo(parent.ObjectType).url(parent.Id);
    }
  };

  return getParentUrl;
};

const useGotoChangeRequestParentUrl = () => {
  const navigate = useNavigate();
  const getParentUrl = useGetChangeRequestParentUrl();
  const gotoParentUrl = async (changeRequest: ChangeRequestRegisterFields) => {
    navigate(
      `${getParentUrl(changeRequest)}?showRequest=true&requestId=${changeRequest.Id}`
    );
  };

  return gotoParentUrl;
};

const useRequestsFields = () => {
  const { t } = useTranslation(['common']);
  const { getByValue } = useRating('approval_status');
  const gotoParentUrl = useGotoChangeRequestParentUrl();
  const approversField = useGetApproversFieldConfig();
  const requestersField = useGetRequestersFieldConfig();
  const currentApproversField = useGetCurrentApproversFieldConfig();
  const nextApproversField = useGetNextApproversFieldConfig();
  const parentOwnersField = useGetParentOwnersFieldConfig();

  const fields: TableFields<ChangeRequestRegisterFields> = {
    SequentialId: {
      id: 'sequentialId',
      header: t('columns.id'),
      cell: (item) =>
        getFriendlyId(Parent_Type_Enum.ChangeRequest, item.SequentialId),
      exportVal: (item) =>
        getFriendlyId(Parent_Type_Enum.ChangeRequest, item.SequentialId),
    },
    ParentSequentialId: {
      id: 'parentSeqId',
      header: t('columns.parentId'),
    },
    ParentName: {
      id: 'parentName',
      header: t('columns.parentName'),
      cell: (item) =>
        item.parent ? (
          <Link
            variant={'secondary'}
            href={'#'}
            onFollow={() => gotoParentUrl(item)}
          >
            {item.ParentName}
          </Link>
        ) : (
          item.ParentName
        ),
    },
    ParentType: {
      id: 'parentType',
      header: t('columns.parentType'),
    },
    parentOwners: parentOwnersField,
    allApprovers: approversField,
    allRequesters: requestersField,
    currentApprovers: currentApproversField,
    nextApprovers: nextApproversField,
    RequiresAction: {
      id: 'requiresAction', // TODO: translation
      header: 'Requires Action',
      ...yesNoColumn('RequiresAction'),
    },
    StatusLabelled: {
      id: 'status',
      header: t('approvals.requestsRegister.columns.status'),
      cell: (item) => (
        <SimpleRatingBadge rating={getByValue(item.ChangeRequestStatus)} />
      ),
    },

    ParentId: {
      id: 'ParentId',
      header: t('approvals.requestsRegister.columns.parentGuid'),
    },
    Workflow: {
      id: 'workflow',
      header: t('approvals.requestsRegister.columns.workflow'),
    },
    approvalConfig: {
      id: 'approvalConfig',
      header: t('approvals.requestsRegister.columns.approvalConfig'),
      cell: (item) => <BadgeList badges={item.approvalConfig} />,
      filterOptions: {
        filteringProperties: {
          operators: (['=', ':'] as PropertyFilterOperator[]).map(
            (operator) => ({
              operator,
              match: (ids: unknown, id: string) => {
                return (ids as string[]).includes(id);
              },
            })
          ),
        },
        filteringOptions: [],
      },
    },
    CreatedAtTimestamp: dateColumnFromConfig({
      header: { header: t('approvals.requestsRegister.columns.dateOpened') },
      dateField: 'CreatedAtTimestamp',
      includeTime: true,
    }),
    DateLastActioned: dateColumnFromConfig({
      header: {
        header: t('approvals.requestsRegister.columns.dateLastActioned'),
      },
      dateField: 'DateLastActioned',
      includeTime: true,
    }),
    DateClosed: dateColumnFromConfig({
      header: { header: t('approvals.requestsRegister.columns.dateClosed') },
      dateField: 'DateClosed',
      includeTime: true,
    }),
    CurrentLevel: {
      id: 'currentLevel',
      header: t('approvals.requestsRegister.columns.currentLevel'),
    },
  };

  return fields;
};

const useRequestsTableProps = (data?: GetChangeRequestsData) => {
  const fields = useRequestsFields();
  const labelledFields = useLabelledFields(data);

  return useMemo<UseGetTablePropsOptions<ChangeRequestRegisterFields>>(() => {
    return {
      fields,
      tableId: 'requestRegister',
      data: labelledFields,
      entityLabel: 'request',
      initialColumns: [
        'SequentialId',
        'ParentName',
        'parentOwners',
        'Workflow',
        'StatusLabelled',
        'CreatedAtTimestamp',
        'DateLastActioned',
        'DateClosed',
      ],
      preferencesStorageKey: 'RequestsRegisterTable-PreferencesV1',
      customAttributeFormIds: [],
    };
  }, [fields, labelledFields]);
};

export const useGetCollectionTableProps = (data?: GetChangeRequestsData) => {
  const props = useRequestsTableProps(data);

  return useGetTableProps(props);
};

export const useGetRequestsSmartWidgetTableProps = (
  data: GetChangeRequestsData | undefined,
  statefulTableOptions: StatefulTableOptions<ChangeRequestRegisterFields>
): TablePropsWithActions<ChangeRequestRegisterFields> => {
  const props = useRequestsTableProps(data);

  return useGetStatelessTableProps<ChangeRequestRegisterFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });
};
